import { HttpResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ExtraSearchParams, IEntitySearchParams, SearchParams } from '@mt-ng2/common-classes';
import { DynamicSearchFilterTypes, DynamicSearchValues, IDynamicSearchFilters, IMetaItem } from '@mt-ng2/dynamic-search-filters';
import { forkJoin, Observable } from 'rxjs';
import { IPickupPaymentDetail } from '../../model/interfaces/pickup-payment-detail';
import { ICarrier } from '../../model/interfaces/carrier';
import { CarrierService } from '../../carriers/services/carrier.service';
import { PickupPaymentDetailService } from '../services/pickup-payment-detail.service';
import { IColumnSortedEvent, IEntity, ISelectionChangedEvent, SortDirection } from '@mt-ng2/entity-list-module';
import { PendingTransactionsEntityListConfig } from './pending-transactions-list.entity-list-config';
import { MtSearchFilterItem } from '@mt-ng2/search-filter-select-control';
import { ISearchFilterDaterangeValue } from '@mt-ng2/search-filter-daterange-control';
import { ICustomer } from '../../model/interfaces/customer';
import { CustomerService } from '../../customers/customer.service';
import { AuthService } from '@mt-ng2/auth-module';
import { OrderService } from '../../orders/order.service';
import { UserRoles } from '../../model/UserRoles';

@Component({
    selector: 'app-pending-transactions-list',
    templateUrl: './pending-transactions-list.component.html',
    styleUrls: ['./pending-transactions-list.component.css'],
    standalone: false
})
export class PendingTransactionsListComponent implements OnInit {
    query = '';
    order = 'DateCreated';
    orderDirection = 'desc';
    currentPage = 1;
    itemsPerPage = 10;
    total = 0;
    startDate: Date | null = null;
    endDate: Date | null = null;
    carriers: ICarrier[] = [];
    selectedCarrierIds: number[] = [];
    customers: ICustomer[] = [];
    selectedCustomerIds: number[] = [];
    pendingTransactions: IPickupPaymentDetail[] = [];
    dynamicSearchFiltersConfig: IDynamicSearchFilters = [];
    datepickerEntityName = 'Date Created';
    entityListConfig!: PendingTransactionsEntityListConfig;
    selectedEntities: IPickupPaymentDetail[] = [];
    isAdminUser = false;

    constructor(
        private pickupPaymentDetailService: PickupPaymentDetailService,
        private carrierService: CarrierService,
        private customerService: CustomerService,
        private authService: AuthService,
        private orderService: OrderService,
    ) {}

    ngOnInit(): void {
        const currentUser = this.authService.currentUser.getValue();
        this.orderService.getAuthUserRole(currentUser.AuthId).subscribe((authUserRole) => {
            this.isAdminUser = authUserRole === UserRoles.Admin || authUserRole === UserRoles.SuperAdmin;
            this.loadInitialData();
        });
    }

    loadInitialData(): void {
        forkJoin({
            carriers: this.carrierService.getActive(),
            customers: this.customerService.getActiveCustomers(),
        }).subscribe((results) => {
            this.carriers = results.carriers;
            this.customers = results.customers;
            this.getTableData();
        });
    }

    getTableData(): void {
        this.entityListConfig = new PendingTransactionsEntityListConfig();
        this.orderDirection = this.entityListConfig.getDefaultSortDirection();
        this.buildSearchBarAndFilters();
        this.getPendingTransactions();
    }

    private buildPendingTransactionsSearchParams(skip: number, take: number): SearchParams {
        const query = typeof this.query === 'string' ? this.query : '';

        const searchEntity: IEntitySearchParams = {
            extraParams: this.buildSearch(),
            order: this.order,
            orderDirection: this.orderDirection,
            skip: skip,
            take: take,
            query: query.length > 0 ? query : '',
        };

        return new SearchParams(searchEntity);
    }

    getPendingTransactionsCall(): Observable<HttpResponse<IPickupPaymentDetail[]>> {
        return this.pickupPaymentDetailService.getPendingTransactions(
            this.buildPendingTransactionsSearchParams((this.currentPage - 1) * this.itemsPerPage, this.itemsPerPage),
        );
    }

    getPendingTransactions(): void {
        this.getPendingTransactionsCall().subscribe((answer) => {
            this.pendingTransactions = answer.body ?? [];
            const totalHeader = answer.headers.get('X-List-Count');
            this.total = totalHeader ? +totalHeader : this.pendingTransactions.length;
        });
    }

    private buildSearch(): ExtraSearchParams[] {
        const _extraSearchParams: ExtraSearchParams[] = [];

        _extraSearchParams.push(
            new ExtraSearchParams({
                name: 'CarrierIds',
                valueArray: this.selectedCarrierIds,
            }),
        );

        _extraSearchParams.push(
            new ExtraSearchParams({
                name: 'CustomerIds',
                valueArray: this.selectedCustomerIds,
            }),
        );

        if (this.startDate) {
            _extraSearchParams.push(
                new ExtraSearchParams({
                    name: 'DateCreatedStartDate',
                    value: this.startDate.toDateString(),
                }),
            );
        }

        if (this.endDate) {
            _extraSearchParams.push(
                new ExtraSearchParams({
                    name: 'DateCreatedEndDate',
                    value: this.endDate.toDateString(),
                }),
            );
        }

        return _extraSearchParams;
    }

    buildSearchBarAndFilters(): void {
        this.dynamicSearchFiltersConfig = [
            {
                Searchbar: {
                    label: 'Search',
                    type: DynamicSearchFilterTypes.Searchbar,
                },
                ...(this.isAdminUser && {
                    Carriers: {
                        label: 'Carrier',
                        type: DynamicSearchFilterTypes.Select,
                        options: { selectOptions: this.mapMtSearchFilterItems(this.carriers, 'Name') },
                    },
                }),
                Customers: {
                    label: 'Customer',
                    type: DynamicSearchFilterTypes.Select,
                    options: { selectOptions: this.mapMtSearchFilterItems(this.customers, 'Name') },
                },
            },
        ];
    }

    pageChanged(): void {
        this.removeAllSubRows();
        this.getPendingTransactions();
    }

    search(evt: DynamicSearchValues): void {
        this.removeAllSubRows();
        this.currentPage = 1;
        this.query = typeof evt.Searchbar === 'string' ? evt.Searchbar : '';
        if (evt.Carriers) {
            this.selectedCarrierIds = (evt.Carriers as IMetaItem[]).map((x) => x.Id);
        }
        if (evt.Customers) {
            this.selectedCustomerIds = (evt.Customers as IMetaItem[]).map((x) => x.Id);
        }
        this.getPendingTransactions();
    }

    dateSelectionChanged(range: ISearchFilterDaterangeValue): void {
        this.removeAllSubRows();
        this.startDate = range.startDate;
        this.endDate = range.endDate;
        this.getPendingTransactions();
    }

    columnSorted(event: IColumnSortedEvent): void {
        this.removeAllSubRows();
        this.order = event.column.sort.sortProperty ?? this.order;
        this.orderDirection = event.column.sort.direction === SortDirection.Desc ? 'desc' : 'asc';
        this.getPendingTransactions();
    }

    private isPickupPayment(detail: IPickupPaymentDetail): boolean {
        return detail?.PickupId != null && detail?.Pickup != null;
    }

    private isDeliveryPayment(detail: IPickupPaymentDetail): boolean {
        return detail?.DeliveryId != null && detail?.Delivery != null;
    }

    selectionChanged(event: ISelectionChangedEvent): void {
        const target = event.$event.target as HTMLElement;
        const eventRow = target.closest('tr');

        if (!eventRow) {
            return;
        }

        const allSubRows = document.querySelectorAll('.sub-row');
        allSubRows.forEach((subRow) => subRow.remove());
        this.selectedEntities = [];

        const selectionFavicon = eventRow.querySelector('i');
        if (selectionFavicon?.classList.contains('fa-check-square-o')) {
            return;
        }

        const subRow = document.createElement('tr');
        subRow.classList.add('sub-row');

        const subCell = document.createElement('td');
        const columnCount = eventRow.getElementsByTagName('td').length;
        subCell.setAttribute('colspan', columnCount.toString());

        const selectedEntity = event.selectedEntities[0] as IPickupPaymentDetail;
        this.selectedEntities.push(selectedEntity);

        let orderNumbers = 'N/A';
        let customerNames = 'N/A';

        if (this.isPickupPayment(selectedEntity)) {
            // Handle Pickup data
            orderNumbers = selectedEntity.Pickup?.Orders?.map((order) => order.OrderNumber).join(', ') || 'N/A';
            customerNames =
                Array.from(
                    new Set(selectedEntity.Pickup?.Orders?.flatMap((order) => (order.Customer?.Name ? [order.Customer.Name] : [])) || []),
                ).join(', ') || 'N/A';
        } else if (this.isDeliveryPayment(selectedEntity)) {
            // Handle Delivery data - deliveries don't have related orders, just a customer
            orderNumbers = 'N/A'; // Deliveries are not linked to orders
            customerNames = selectedEntity.Delivery?.Customer?.Name || 'N/A';
        }

        subCell.innerHTML = `
            <div class="detail-content">
                <div class="detail-item"><strong>Order Numbers:</strong> <span>${orderNumbers}</span></div>
                <div class="detail-item"><strong>Customers:</strong> <span>${customerNames}</span></div>
                <div><strong>Payment Status:</strong> Pending (Credit Transaction)</div>
            </div>
        `;

        subRow.appendChild(subCell);
        eventRow.parentNode?.insertBefore(subRow, eventRow.nextSibling);
    }

    removeAllSubRows(): void {
        const allSubRows = document.querySelectorAll('.sub-row');
        allSubRows.forEach((subRow) => subRow.remove());
        this.selectedEntities = [];
    }

    private getAllPendingTransactionsCall(): Observable<HttpResponse<IPickupPaymentDetail[]>> {
        const exportCount = this.total > 0 ? this.total : Math.max(this.pendingTransactions.length, this.itemsPerPage);
        return this.pickupPaymentDetailService.getPendingTransactions(this.buildPendingTransactionsSearchParams(0, exportCount));
    }

    exportToExcel(): void {
        this.getAllPendingTransactionsCall().subscribe((response) => {
            const data = response.body ?? [];
            const headers = ['Pickup/Delivery Number', 'Related Order #', 'Customer', 'Date Created', 'Pending Amount', 'Carrier', 'Driver'];

            const csvContent = this.convertToCSV(data, headers);
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `pending-transactions-${new Date().toISOString().split('T')[0]}.csv`);
            link.className = 'hidden-download-link';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        });
    }

    private convertToCSV(data: IPickupPaymentDetail[], headers: string[]): string {
        const csvArray = [headers.join(',')];

        data.forEach((item) => {
            let pickupDeliveryNumber = 'N/A';
            let orderNumbers = 'N/A';
            let uniqueCustomers = 'N/A';
            let carrier = 'N/A';

            if (this.isPickupPayment(item)) {
                // Handle Pickup data
                pickupDeliveryNumber = item?.Pickup?.PickupNumber ?? 'N/A';
                const orders = item?.Pickup?.Orders ?? [];
                orderNumbers = orders.map((order) => order.OrderNumber).join('; ') || 'N/A';
                uniqueCustomers =
                    Array.from(new Set(orders.flatMap((order) => (order.Customer?.Name ? [order.Customer.Name] : [])))).join('; ') || 'N/A';
                carrier = item?.Pickup?.Carrier?.Name ?? 'N/A';
            } else if (this.isDeliveryPayment(item)) {
                // Handle Delivery data
                pickupDeliveryNumber = item?.Delivery?.DeliveryNumber ?? 'N/A';
                orderNumbers = 'N/A'; // Deliveries are not linked to orders
                uniqueCustomers = item?.Delivery?.Customer?.Name ?? 'N/A';
                carrier = item?.Delivery?.Carrier?.Name ?? 'N/A';
            }

            const firstName = item?.Pickup?.Driver?.FirstName?.trim() ?? '';
            const lastName = item?.Pickup?.Driver?.LastName?.trim() ?? '';
            const fullName = [firstName, lastName].filter((name) => name.length > 0).join(' ') || 'N/A';

            const row = [
                `"${pickupDeliveryNumber}"`,
                `"${orderNumbers}"`,
                `"${uniqueCustomers}"`,
                `"${new Date(item.DateCreated).toLocaleDateString()}"`,
                `"$${item.Amount.toFixed(2)}"`,
                `"${carrier}"`,
                `"${fullName}"`,
            ];

            csvArray.push(row.join(','));
        });

        return csvArray.join('\n');
    }

    private mapMtSearchFilterItems(items: IEntity[], key: string): MtSearchFilterItem[] {
        return items?.map((item) => {
            return new MtSearchFilterItem(
                {
                    Id: item.Id,
                    Name: (item as unknown as Record<string, unknown>)[key] as string,
                },
                false,
            );
        });
    }
}
